import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../services/api";
import PrescriptionPrintCombined from "./PrescriptionPrintCombined";

export default function PatientHistory() {
  const { patientId } = useParams();
  const navigate = useNavigate();

  const [patient,   setPatient]   = useState(null);
  const [history,   setHistory]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [loadError, setLoadError] = useState("");

  /* Inline print preview state */
  const [previewRx,      setPreviewRx]      = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => { load(); }, [patientId]);

  const load = async () => {
    setLoading(true); setLoadError("");
    try {
      const [pRes, hRes] = await Promise.all([
        api.get(`/patients/${patientId}`),
        api.get(`/prescriptions/patient/${patientId}`),
      ]);
      setPatient(pRes.data);
      setHistory(hRes.data);
    } catch (err) {
      setLoadError(err.response?.data?.message || "Failed to load patient data");
    } finally {
      setLoading(false);
    }
  };

  /*
    VIEW PRESCRIPTION FIX:
    Old code used window.open() which creates a new tab.
    New tabs do NOT share sessionStorage (browser security).
    So doctorToken was missing in the new tab → 401 → blank page.

    Fix: fetch the RX data here (in the authenticated context)
    and display it in an inline modal. The doctor can print from
    the modal's Print button which opens a clean print window
    with the content already injected — no auth needed.
  */
  const viewPrescription = async (rx) => {
    setLoadingPreview(true);
    try {
      const res = await api.get(`/prescriptions/${rx.prescriptionId}`);
      setPreviewRx(res.data);
    } catch (err) {
      alert("Could not load prescription: " + (err.response?.data?.message || "Unknown error"));
    } finally {
      setLoadingPreview(false);
    }
  };

  const printFromModal = () => {
    if (!previewRx) return;
    // Open PrescriptionPrintRouter — it re-renders vectors client-side.
    // No stored images are needed; the pages (stroke arrays) come from MongoDB.
    const win = window.open("/print/handwritten", "_blank", "width=900,height=750");
    const send = () => win.postMessage({ type: "RX_PRINT", payload: previewRx }, "*");
    const handler = (e) => {
      if (e.source === win && e.data?.type === "PRINT_READY") {
        send();
        window.removeEventListener("message", handler);
      }
    };
    window.addEventListener("message", handler);
    setTimeout(send, 1500); // fallback
  };

  const continueFollowup = (rx) => {
    navigate(`/staff-portal/prescription/${patientId}?visitType=followup&parentRxId=${rx.prescriptionId}`);
  };

  /* ── Render states ── */
  if (loading) return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="text-gray-400 text-sm">Loading patient history…</div>
    </div>
  );

  if (loadError) return (
    <div className="card p-8 text-center">
      <p className="text-red-500 mb-3">{loadError}</p>
      <button onClick={() => navigate(-1)} className="btn text-sm">← Go Back</button>
    </div>
  );

  if (!patient) return (
    <div className="card p-8 text-center text-gray-400">
      Patient not found.
      <button onClick={() => navigate(-1)} className="btn text-sm ml-3">← Back</button>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">

      {/* ── Header ── */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">{patient.name}</h1>
          <p className="text-sm text-gray-500">
            {patient.patientId}
            {patient.age    ? ` · ${patient.age} yrs`  : ""}
            {patient.gender ? ` · ${patient.gender}`   : ""}
            {patient.phone  ? ` · ${patient.phone}`    : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/staff-portal/patient/edit/${patientId}`)}
            className="btn text-sm border border-blue-300 text-blue-600 hover:bg-blue-50"
          >
            ✏️ Edit Patient
          </button>
          <button
            onClick={() => navigate(`/staff-portal/prescription/${patientId}?visitType=new`)}
            className="btn-primary text-sm"
          >
            + New Visit
          </button>
          <button onClick={() => navigate(-1)} className="btn text-sm">← Back</button>
        </div>
      </div>

      {/* ── Empty state ── */}
      {history.length === 0 && (
        <div className="card text-center text-gray-400 py-12">
          <p className="text-3xl mb-2">📋</p>
          <p className="text-sm font-medium">No prescriptions yet</p>
          <p className="text-xs mt-1">Start a new visit to create the first prescription for this patient.</p>
        </div>
      )}

      {/* ── Timeline ── */}
      <div className="space-y-3">
        {history.map((rx, i) => (
          <motion.div
            key={rx._id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className="card bg-white rounded-xl border border-gray-100 shadow-sm p-4"
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-800">
                    {new Date(rx.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    rx.visitType === "followup" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"
                  }`}>
                    {rx.visitType === "followup" ? "Follow-up" : "New Visit"}
                  </span>
                  {rx.visitNumber && (
                    <span className="text-xs text-gray-400">Visit #{rx.visitNumber}</span>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-0.5">Rx# {rx.prescriptionId}</p>
                {rx.typed?.complaints && (
                  <p className="text-xs text-gray-400 mt-1 line-clamp-1">
                    {rx.typed.complaints.replace(/<[^>]+>/g, "").substring(0, 80)}
                  </p>
                )}
              </div>

              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => viewPrescription(rx)}
                  disabled={loadingPreview}
                  className="btn text-sm px-3 disabled:opacity-50"
                >
                  {loadingPreview ? "…" : "👁 View"}
                </button>
                <button
                  onClick={() => continueFollowup(rx)}
                  className="btn-primary text-sm px-3"
                >
                  ↩ Follow-up
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* ── Inline prescription preview modal ── */}
      <AnimatePresence>
        {previewRx && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setPreviewRx(null)}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-4 md:inset-8 bg-gray-100 rounded-2xl z-50 flex flex-col shadow-2xl overflow-hidden"
            >
              {/* Modal top bar */}
              <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-gray-100 shrink-0">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Prescription — {previewRx.prescriptionId}</p>
                  <p className="text-xs text-gray-400">
                    {previewRx.patient?.name} ·{" "}
                    {new Date(previewRx.createdAt).toLocaleDateString("en-IN", { day:"numeric",month:"short",year:"numeric" })}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={printFromModal} className="btn-primary text-sm px-4 py-2">🖨 Print</button>
                  <button onClick={() => setPreviewRx(null)} className="btn text-sm px-3 py-2">✕ Close</button>
                </div>
              </div>

              {/* Scrollable A4 preview */}
              <div className="flex-1 overflow-auto p-4 flex justify-center">
                <div
                  style={{
                    transform: "scale(0.72)",
                    transformOrigin: "top center",
                    width: "794px",
                    minHeight: "1123px",
                  }}
                >
                  {/* This div is what we clone into the print window */}
                  <div id="inline-print-content">
                    <PrescriptionPrintCombined rx={previewRx} />
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </motion.div>
  );
}